import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "@/shared/hooks/use-toast";
import { logger } from "@/shared/lib/logger";

import {
  configIncompleteDetails,
  errorDetails,
  isDraftInvalid,
  websiteService,
  type Checklist,
  type FieldSpec,
  type Question,
  type SiteDetail,
  type Template,
} from "../../api/websiteService";
import { needsPrefill, type QuestionAnswer } from "../../model/collect";
import { rowEditAnswer } from "../../model/rowEdit";
import { preflightWarningText } from "../components/host/hostLabels";

/** 目标题写的字段前缀(api `WebsiteAnswerService`:goal.business / goal.secondary / goal.cta 回写站点行)。 */
const GOAL_PATH_PREFIX = "goal.";

/** 正在路上的写口:同一时刻只放一个(附录 A.36:collect_state 没有乐观锁,由 console 串行提交兜底)。 */
export type CollectBusy = "prefill" | "answer" | "generate" | null;

/** 答过的一题(画成「问 + 你的回答」两个气泡)。`key` 本地自增:同一道题可能被 api 重新出、再答一次。 */
export interface AnsweredEntry {
  key: number;
  question: Question;
  labels: string[];
}

/**
 * 屏 3 的数据流:进页加载(getSite → checklist + 模板)、自动 / 手动预填、答题、出稿。
 *
 * ★ **串行**:预填 / 答题 / 出稿三个写口同一时刻只有一个在路上(`lockRef` 挡连点,`busy` 驱动页面把所有题卡
 *   和页头按钮一起禁掉)。两个人 / 两张卡同时写 collect_state 会互相覆盖。
 * ★ **题目以 api 为准**:每次回包整份换清单,页面画的就是最新清单里的 `questions`(减去本地跳过的)。
 *   **不按「本地答过的 id」再过滤** —— api 已经把答完的题去掉了;它还给同一个 id,说明这题还没答完
 *   (比如只补了一部分),题卡就得再出来。
 * ★ **按站点 id 防串台**:路由从 /sites/1 换到 /sites/2 时组件不卸载,旧请求晚到的回包一律丢掉
 *   (`alive(id)`),答题日志 / 跳过集合跟着清空。
 * ★ `bootRef` 按 id 挡 StrictMode 的二次 effect:自动预填(写口,有成本)不会跑两遍。
 * ★ 预填成功后清单直接用 prefill 的回包,不再 GET:`warnings` 只在 prefill / answers 回包里(附录 A.33)。
 * ★ 答的是目标题(`fieldPaths` 落在 `goal.*`)时顺手重拉站点:api 把 goal.* 回写 `xb_site.business_goal`
 *   等列,页头的「主目标」跟着变。重拉失败只记日志,不打断答题。
 */
export function useCollectFlow(id: string) {
  const navigate = useNavigate();
  const [site, setSite] = useState<SiteDetail | null>(null);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [fields, setFields] = useState<Map<string, FieldSpec> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<CollectBusy>(null);
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const [answered, setAnswered] = useState<AnsweredEntry[]>([]);
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set());
  const idRef = useRef(id);
  const bootRef = useRef<string | null>(null);
  const lockRef = useRef(false);
  const seqRef = useRef(0);

  const alive = useCallback((forId: string) => idRef.current === forId, []);

  /** 拿锁;已有写口在路上回 false(调用方直接放弃,不排队 —— 按钮本来就是禁用的,这里只挡连点)。 */
  const begin = useCallback((kind: Exclude<CollectBusy, null>): boolean => {
    if (lockRef.current) return false;
    lockRef.current = true;
    setBusy(kind);
    return true;
  }, []);

  /** 放锁;站点已经换了就不碰(换站时锁已重置,别把新站的锁放掉)。 */
  const end = useCallback(
    (forId: string) => {
      if (!alive(forId)) return;
      lockRef.current = false;
      setBusy(null);
      setAnsweringId(null);
    },
    [alive]
  );

  const runPrefill = useCallback(async () => {
    if (!begin("prefill")) return;
    try {
      const next = await websiteService.prefill(id);
      if (alive(id)) setChecklist(next);
    } catch (e) {
      if (!alive(id)) return;
      toast({
        title: "知识库预填失败,可稍后重试",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      end(id);
    }
  }, [id, alive, begin, end]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const s = await websiteService.getSite(id);
      if (!alive(id)) return;
      setSite(s);
      // 模板只为拿字段的 `default`(清单「默认:…」那一格),拉不到不挡页面。
      const [cl] = await Promise.all([
        websiteService.checklist(id),
        websiteService
          .getTemplate(s.templateCode)
          .then((t) => {
            if (alive(id)) setFields(indexFields(t));
          })
          .catch((e: unknown) =>
            logger.warn("[website] 模板拉不到,清单不画默认值", { error: String(e) })
          ),
      ]);
      if (!alive(id)) return;
      setChecklist(cl);
      if (needsPrefill(cl)) await runPrefill();
    } catch (e) {
      if (alive(id)) setError(e instanceof Error ? e.message : String(e));
    }
  }, [id, alive, runPrefill]);

  useEffect(() => {
    idRef.current = id;
    if (bootRef.current === id) return;
    if (bootRef.current !== null) {
      // 换站:上一站的一切本地状态作废(晚到的回包由 alive 挡掉)。
      lockRef.current = false;
      setSite(null);
      setChecklist(null);
      setFields(null);
      setBusy(null);
      setAnsweringId(null);
      setAnswered([]);
      setSkipped(new Set());
    }
    bootRef.current = id;
    void load();
  }, [id, load]);

  /** 重拉站点(目标题答完后);失败只记日志。 */
  const refreshSite = useCallback(
    async (forId: string) => {
      try {
        const s = await websiteService.getSite(forId);
        if (alive(forId)) setSite(s);
      } catch (e) {
        logger.warn("[website] 答目标题后重拉站点失败", { error: String(e) });
      }
    },
    [alive]
  );

  const answer = useCallback(
    async (q: Question, a: QuestionAnswer) => {
      if (!begin("answer")) return;
      setAnsweringId(q.id);
      try {
        const next = await websiteService.answer(id, a.answers);
        if (!alive(id)) return;
        setChecklist(next);
        seqRef.current += 1;
        const key = seqRef.current;
        setAnswered((prev) => [...prev, { key, question: q, labels: a.labels }]);
        if (q.fieldPaths.some((p) => p.startsWith(GOAL_PATH_PREFIX))) void refreshSite(id);
      } catch (e) {
        if (!alive(id)) return;
        toast({
          title: "回答没保存上",
          description: e instanceof Error ? e.message : String(e),
          variant: "destructive",
        });
      } finally {
        end(id);
      }
    },
    [id, alive, begin, end, refreshSite]
  );

  /**
   * 清单行内直接改一项(0925 B7):同一把串行锁、同一个 answers 端点(`row:{path}` 题号,见 model/rowEdit),
   * 回包整份换清单。回 true = 已保存(编辑框收起);失败 toast、回 false(框留着,输入不丢)。
   */
  const editRow = useCallback(
    async (path: string, value: unknown): Promise<boolean> => {
      if (!begin("answer")) return false;
      try {
        const next = await websiteService.answer(id, [rowEditAnswer(path, value)]);
        if (!alive(id)) return false;
        setChecklist(next);
        return true;
      } catch (e) {
        if (alive(id)) {
          toast({
            title: "这一项没保存上",
            description: e instanceof Error ? e.message : String(e),
            variant: "destructive",
          });
        }
        return false;
      } finally {
        end(id);
      }
    },
    [id, alive, begin, end]
  );

  const skip = useCallback((questionId: string) => {
    setSkipped((prev) => new Set(prev).add(questionId));
  }, []);

  const generate = useCallback(
    async (allowPlaceholders: boolean) => {
      if (!begin("generate")) return;
      try {
        const result = await websiteService.generateDraft(id, { allowPlaceholders });
        if (!alive(id)) return;
        // 出稿已扣费成功:回包形状再不对也不能挡住跳转,warnings 读不到就当没有。
        // 带码的(TEMPLATE_FACT_CLEARED {token} 这类)翻成人话;认不出的整句原样(同发布体检口径)。
        const notes = (result?.warnings ?? [])
          .filter((w) => w.trim() !== "")
          .map(preflightWarningText);
        toast({
          title: "初稿已生成",
          ...(notes.length > 0 ? { description: notes.join("\n") } : {}),
        });
        navigate(`/ai-website/sites/${id}/edit`);
      } catch (e) {
        if (alive(id)) toast({ ...draftFailure(e), variant: "destructive" });
      } finally {
        end(id);
      }
    },
    [id, alive, begin, end, navigate]
  );

  return {
    site,
    checklist,
    fields,
    error,
    busy,
    answeringId,
    answered,
    skipped,
    load,
    runPrefill,
    answer,
    editRow,
    skip,
    generate,
  };
}

/**
 * 出稿失败的 toast:两种 422 同码不同义(附录 A.35⑤ / api `WebsiteDraftService`)——
 * 「初稿没通过校验」是 ai 出的稿不合规(重新生成),「资料还不够出稿」是缺必填事实(回去补)。
 */
function draftFailure(e: unknown): { title: string; description: string } {
  const message = e instanceof Error ? e.message : String(e);
  const details = errorDetails(e);
  const described = details.length ? details.join("\n") : message;
  if (isDraftInvalid(e)) return { title: "初稿没通过校验", description: described };
  if (configIncompleteDetails(e)) return { title: "资料还不够出稿", description: described };
  const status = (e as { httpStatus?: unknown } | null)?.httpStatus;
  if (status === 403) return { title: "建站舱未开通", description: message };
  return { title: "生成初稿失败", description: message };
}

function indexFields(t: Template): Map<string, FieldSpec> {
  return new Map(t.schema.flatMap((g) => g.fields).map((f) => [f.path, f]));
}
